import {
  Spin,
  Form,
  Row,
  Col,
  message,
  Select,
  Collapse,
  Space,
  Empty,
  Modal,
} from "antd";
import {
  HomeOutlined,
  FileTextOutlined,
  ReadOutlined,
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  LayoutOutlined,
  CopyOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import React, { Component, useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  FormItem,
  InputItem,
  ButtonItem,
  SelectItem,
  SwitchItem,
  SidebarMenu,
} from "../../../../Components/Items";
import RichTextEditor from "../../../../Components/RichTextEditor";
import axios from "axios";
import DynamicCardForm from "../../../../Components/DynamicCardForm";
import ZonaFieldsRenderer from "./ZonaFieldsRenderer";
import {
  ZONA_OPTIONS,
  getVarianteOptions,
  getVarianteDescripcion,
  getZonaTitulo,
  procesarZonas,
  procesarLayout,
  getLayoutVarianteOptions,
  getLayoutVarianteDescripcion,
  getLayoutVarianteFields,
  getSubHeaderTipoOptions,
} from "./componentesConfig";

const { Option } = Select;
const { Panel } = Collapse;

// Editor para páginas legales que permite alternar entre el editor visual
// (RichTextEditor) y la edición/pegado de HTML crudo. Replica el patrón usado
// en EditarInsumo (DescripcionWebEditor). Es un componente controlado: recibe
// `value`/`onChange` desde el FormItem que lo envuelve, por lo que el valor se
// comparte entre ambos modos sin perder el contenido al alternar.
function EditorPaginaConHtml({ value, onChange, placeholder }) {
  const [htmlMode, setHtmlMode] = useState(false);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 4,
        }}
      >
        <button
          type="button"
          onClick={() => setHtmlMode((prev) => !prev)}
          style={{
            fontSize: 12,
            padding: "2px 10px",
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            background: htmlMode ? "#00BFD6" : "#fff",
            color: htmlMode ? "#fff" : "#595959",
            cursor: "pointer",
          }}
        >
          {htmlMode ? "Vista visual" : "HTML crudo"}
        </button>
      </div>
      {htmlMode ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          rows={14}
          style={{
            width: "100%",
            fontFamily: "monospace",
            fontSize: 12,
            border: "1px solid #d9d9d9",
            borderRadius: 4,
            padding: 8,
            resize: "vertical",
            boxSizing: "border-box",
          }}
          placeholder="Pega o escribe el HTML aquí..."
        />
      ) : (
        <RichTextEditor
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

// Prompt de ejemplo que el usuario puede copiar y pegar en una IA para generar
// el HTML de una página legal compatible con el render de la tienda online.
const PROMPT_EJEMPLO_HTML = `Genera el contenido HTML de una página legal (términos, aviso de privacidad, envíos, etc.) para una tienda online. El HTML se renderiza dentro de un contenedor con la clase "prose" de Tailwind Typography, así que los títulos, párrafos, listas y enlaces YA se ven bien sin clases. Reglas:

ESTRUCTURA
- Devuelve SOLO el contenido (sin <html>, <head>, <body>, <style>, <script>).
- Usa HTML semántico: <h2>, <h3>, <p>, <ul>, <ol>, <li>, <strong>, <em>, <a href="..." target="_blank" rel="noopener noreferrer">, <hr>, <blockquote>.
- NO uses <h1> (el título de la página ya lo pone el sistema).

══════════════════════════════════════════════════════════════════
COLORES — hay DOS formas, según el color que quieras:
══════════════════════════════════════════════════════════════════
1) COLORES DE LA MARCA → usa CLASES de Tailwind (de la lista permitida):
   Texto: text-primary-700 text-primary-500 text-secondary-700 text-tertiary-700
   Fondo: bg-primary-50 bg-primary-500 bg-secondary-50 bg-tertiary-50
   Ejemplo: <p class="text-primary-700 font-semibold">Texto en color de marca</p>

2) CUALQUIER OTRO COLOR → usa ESTILO INLINE (no una clase):
   Texto: <span style="color: #e60000;">...</span>
   Fondo: <span style="background-color: #fff3cd;">...</span>
   Ejemplo: <p style="color: #6b21a8;">Texto en un morado personalizado</p>

REGLA: en un mismo elemento NO mezcles clase de color Y style de color
(si pones ambos, gana el inline). Elige uno por elemento.
══════════════════════════════════════════════════════════════════

CLASES TAILWIND PERMITIDAS (para todo lo demás: tamaño, espaciado, layout)
- Alineación: text-left text-center text-right text-justify
- Tamaño/peso: text-sm text-base text-lg text-xl text-2xl text-3xl font-medium font-semibold font-bold italic uppercase tracking-wide leading-relaxed leading-loose
- Colores texto marca: text-primary-700 text-primary-500 text-secondary-700 text-tertiary-700 text-neutral-500 text-neutral-600 text-neutral-700 text-white
- Fondos marca: bg-primary-50 bg-primary-500 bg-secondary-50 bg-tertiary-50 bg-neutral-50 bg-neutral-100 bg-white
- Bordes/radio: border border-2 border-l-4 border-primary-500 border-neutral-200 rounded rounded-lg rounded-xl
- Espaciado: p-2 p-3 p-4 p-6 px-4 py-2 py-3 my-4 my-6 mb-4 mt-4
- Layout: flex items-center gap-2 gap-3 grid grid-cols-2 w-full
- Sombras: shadow-sm shadow shadow-md
- Listas: list-disc list-decimal list-inside

PROHIBIDO
- NO inventes CLASES fuera de la lista de arriba (no se genera el CSS y no se ven). Esto aplica a clases; los colores libres SÍ se permiten, pero SOLO vía style inline.
- NO uses colores arbitrarios como CLASE (text-[#123456] no funciona) → usa style inline.
- NO uses px arbitrarios tipo p-[15px]; solo los valores de espaciado listados.

IDEAS DE ESTILO
- Caja destacada con color de marca:
  <div class="bg-primary-50 border-l-4 border-primary-500 p-4 rounded-lg my-6">
    <p class="text-primary-700 font-semibold">Texto importante aquí.</p>
  </div>
- Caja destacada con color libre (inline):
  <div style="background-color: #fff3cd; border-left: 4px solid #f59e0b;" class="p-4 rounded-lg my-6">
    <p style="color: #92400e;" class="font-semibold">Aviso en tono ámbar personalizado.</p>
  </div>
- Usa <hr> para separar secciones grandes.`;

// Bloque colapsable que muestra el prompt de ejemplo y un botón para copiarlo
// al portapapeles. Pensado para acompañar a cada página legal.
function PromptEjemploCopiable() {
  const copiarPrompt = () => {
    const exito = () => message.success("Prompt copiado al portapapeles");
    const fallo = () => message.error("No se pudo copiar el prompt");

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(PROMPT_EJEMPLO_HTML).then(exito, fallo);
    } else {
      // Fallback para navegadores/contextos sin clipboard API
      try {
        const ta = document.createElement("textarea");
        ta.value = PROMPT_EJEMPLO_HTML;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        exito();
      } catch (e) {
        fallo();
      }
    }
  };

  return (
    <Collapse
      bordered={false}
      style={{ background: "#f5fbfd", borderRadius: 6, marginBottom: 16 }}
    >
      <Panel
        key="prompt"
        header={
          <span style={{ fontWeight: 600, color: "#00838f" }}>
            Prompt de ejemplo para generar el HTML con IA
          </span>
        }
        extra={
          <div onClick={(e) => e.stopPropagation()}>
            <ButtonItem
              icon={<CopyOutlined />}
              onClick={copiarPrompt}
              cyan
              lessTime
            >
              Copiar prompt
            </ButtonItem>
          </div>
        }
      >
        <pre
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "monospace",
            fontSize: 12,
            color: "#444",
            background: "#fff",
            border: "1px solid #e6f4f6",
            borderRadius: 4,
            padding: 12,
            margin: 0,
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          {PROMPT_EJEMPLO_HTML}
        </pre>
      </Panel>
    </Collapse>
  );
}

function BannerEscuelaCard({ escuela, empresaId, headersToken, normFile, normColor }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tieneConfig, setTieneConfig] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .post(
        "configuracionTiendaOnline/obtenerConfiguracionBannerCarritoEscuela",
        { empresaId, escuelaId: escuela._id },
        { headers: headersToken },
      )
      .then((res) => {
        if (res.data.success === 2 && res.data.data) {
          setTieneConfig(true);
          form.setFieldsValue(res.data.data);
        } else {
          form.setFieldsValue({
            bannerCarrito: {
              superior: { activo: false, variante: undefined, datos: {} },
              inferior: { activo: false, variante: undefined, datos: {} },
            },
          });
        }
      })
      .catch(() => message.error("Error al cargar configuración de escuela"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escuela._id]);

  const guardar = async () => {
    setLoading(true);
    try {
      const values = form.getFieldsValue(true);
      const layoutProcesado = await procesarLayout({ bannerCarrito: values.bannerCarrito });
      const res = await axios.post(
        "configuracionTiendaOnline/actualizarConfiguracionBannerCarritoEscuela",
        {
          empresaId,
          escuelaId: escuela._id,
          bannerCarrito: layoutProcesado.bannerCarrito,
        },
        { headers: headersToken },
      );
      if (res.data.success === 2) {
        message.success("Configuración de banner guardada");
        setTieneConfig(true);
      } else {
        message.error(res.data.message || "No se pudo guardar la configuración");
      }
    } catch {
      message.error("Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const eliminar = () => {
    Modal.confirm({
      title: "¿Eliminar configuración personalizada?",
      content:
        "La escuela dejará de tener banners propios en el carrito. Sus estudiantes no verán ningún banner hasta que configures uno nuevo.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      centered: true,
      onOk: async () => {
        setLoading(true);
        try {
          await axios.post(
            "configuracionTiendaOnline/eliminarConfiguracionBannerCarritoEscuela",
            { empresaId, escuelaId: escuela._id },
            { headers: headersToken },
          );
          message.success("Configuración eliminada");
          setTieneConfig(false);
          form.setFieldsValue({
            bannerCarrito: {
              superior: { activo: false, variante: undefined, datos: {} },
              inferior: { activo: false, variante: undefined, datos: {} },
            },
          });
        } catch {
          message.error("Error al eliminar");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const renderSlot = (slot, titulo) => {
    const activoPath = ["bannerCarrito", slot, "activo"];
    const variantePath = ["bannerCarrito", slot, "variante"];

    return (
      <DynamicCardForm title={titulo} style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <span style={{ fontWeight: "bold" }}>Mostrar este banner</span>
          <FormItem name={activoPath} valuePropName="checked" margin={false}>
            <SwitchItem />
          </FormItem>
        </Row>

        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) =>
            prev?.bannerCarrito?.[slot]?.activo !==
            curr?.bannerCarrito?.[slot]?.activo
          }
        >
          {({ getFieldValue }) => {
            if (!getFieldValue(activoPath)) return null;
            return (
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev?.bannerCarrito?.[slot]?.variante !==
                  curr?.bannerCarrito?.[slot]?.variante
                }
              >
                {({ getFieldValue: gfv }) => {
                  const variante = gfv(variantePath);
                  const descripcion = getVarianteDescripcion("bannerCarrito", variante);
                  return (
                    <>
                      <FormItem
                        label="Tipo de banner"
                        name={variantePath}
                        tooltip="Elige si será una sola imagen alargada o tres imágenes unidas."
                        required
                      >
                        <SelectItem
                          placeholder="Selecciona el tipo de banner"
                          options={getVarianteOptions("bannerCarrito")}
                          onChange={() =>
                            form.setFields([
                              {
                                name: ["bannerCarrito", slot, "datos"],
                                value: {},
                              },
                            ])
                          }
                        />
                      </FormItem>
                      {descripcion && (
                        <div
                          style={{
                            background: "#f5fbfd",
                            borderLeft: "3px solid #00BFD6",
                            padding: "8px 12px",
                            borderRadius: 4,
                            marginBottom: 16,
                            fontSize: 12,
                            color: "#555",
                            lineHeight: 1.5,
                          }}
                        >
                          {descripcion}
                        </div>
                      )}
                      {variante && (
                        <ZonaFieldsRenderer
                          tipo="bannerCarrito"
                          variante={variante}
                          parentName={["bannerCarrito", slot, "datos"]}
                          normFile={normFile}
                          normColor={normColor}
                        />
                      )}
                    </>
                  );
                }}
              </Form.Item>
            );
          }}
        </Form.Item>
      </DynamicCardForm>
    );
  };

  return (
    <Spin spinning={loading}>
      <Form form={form} layout="vertical">
        {renderSlot("superior", "Banner superior (bajo el header)")}
        {renderSlot("inferior", "Banner inferior (final de la página)")}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 8,
          }}
        >
          {tieneConfig && (
            <ButtonItem rojo lessTime onClick={eliminar}>
              Eliminar configuración
            </ButtonItem>
          )}
          <ButtonItem type="primary" onClick={guardar}>
            Guardar para esta escuela
          </ButtonItem>
        </div>
      </Form>
    </Spin>
  );
}

// ── Menú y títulos ──────────────────────────────────────────────────────────
const MENU_ITEMS = [
  { key: "layout", icon: <LayoutOutlined />, label: "Layout" },
  { key: "inicio", icon: <HomeOutlined />, label: "Página de Inicio" },
  { key: "carrito", icon: <ShoppingCartOutlined />, label: "Carrito" },
  { key: "terminos", icon: <ReadOutlined />, label: "Términos y Condiciones" },
  { key: "aviso", icon: <ReadOutlined />, label: "Aviso de Privacidad" },
  {
    key: "enviosDevoluciones",
    icon: <FileTextOutlined />,
    label: "Envíos y Devoluciones",
  },
  { key: "metodosPago", icon: <FileTextOutlined />, label: "Métodos de Pago" },
  { key: "sobreNosotros", icon: <FileTextOutlined />, label: "Sobre Nosotros" },
  {
    key: "prox",
    icon: <FileTextOutlined />,
    label: "(Próximamente)",
    disabled: true,
  },
];

const SECTION_TITLES = {
  layout: "Layout",
  inicio: "Página de Inicio",
  carrito: "Componentes del Carrito",
  terminos: "Términos y Condiciones",
  aviso: "Aviso de Privacidad",
  enviosDevoluciones: "Envíos y Devoluciones",
  metodosPago: "Métodos de Pago",
  sobreNosotros: "Sobre Nosotros",
};

class PaginasConfiguracion extends Component {
  formRef = React.createRef();
  state = {
    loading: false,
    formConCambios: false,
    marcasDisponibles: [],
    tipoNosotros: "texto",
    escuelasDisponibles: [],
  };

  componentDidMount() {
    this.consultarConfiguracion();
    this.consultarMarcas();
    if (this.props.escuela) {
      this.consultarEscuelas();
    }
  }

  consultarConfiguracion = () => {
    this.setState({ loading: true });
    axios
      .post(
        "configuracionTiendaOnline/obtenerConfiguracionPaginas",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (res.data.success === 2 && res.data.data) {
          const data = res.data.data;

          const sn = data.paginas?.sobreNosotros ?? data.sobreNosotros;
          const esString = typeof sn === "string";
          const snTipo = esString ? "texto" : sn?.tipo || "texto";
          const snTexto = esString ? sn : sn?.texto || "";
          const snUrl = esString ? "" : sn?.url || "";

          this.setState({ tipoNosotros: snTipo });

          // El backend almacena en shape de storage (paginas.inicio.contenido)
          // pero el form trabaja con shape de API (inicio.zonas), por lo que
          // mapeamos cada bloque convirtiendo `activo` → `mostrar`.
          const contenidoBackend = Array.isArray(
            data.paginas?.inicio?.contenido,
          )
            ? data.paginas.inicio.contenido
            : Array.isArray(data.inicio?.zonas)
              ? data.inicio.zonas
              : [];

          const zonasForm = contenidoBackend.map((b) => ({
            tipo: b.tipo,
            variante: b.variante,
            mostrar: b.mostrar ?? b.activo ?? true,
            tituloInterno: b.tituloInterno || "",
            datos: b.datos || {},
          }));

          this.formRef.current.setFieldsValue({
            layout: {
              header: {
                variante: data.layout?.header?.variante || "HeaderBase",
                datos: data.layout?.header?.datos || {},
              },
              subHeader: {
                activo: data.layout?.subHeader?.activo ?? false,
                tipo: data.layout?.subHeader?.tipo || undefined,
                variante: data.layout?.subHeader?.variante || undefined,
                datos: data.layout?.subHeader?.datos || {},
              },
              footer: {
                variante: data.layout?.footer?.variante || "FooterBase",
                datos: data.layout?.footer?.datos || {},
              },
              bannerCarrito: {
                superior: {
                  activo: data.layout?.bannerCarrito?.superior?.activo ?? false,
                  variante:
                    data.layout?.bannerCarrito?.superior?.variante || undefined,
                  datos: data.layout?.bannerCarrito?.superior?.datos || {},
                },
                inferior: {
                  activo: data.layout?.bannerCarrito?.inferior?.activo ?? false,
                  variante:
                    data.layout?.bannerCarrito?.inferior?.variante || undefined,
                  datos: data.layout?.bannerCarrito?.inferior?.datos || {},
                },
              },
            },
            inicio: {
              zonas: zonasForm,
            },
            terminos: data.paginas?.terminos ?? data.terminos ?? "",
            aviso: data.paginas?.aviso ?? data.aviso ?? "",
            enviosDevoluciones:
              data.paginas?.enviosDevoluciones ?? data.enviosDevoluciones ?? "",
            metodosPago: data.paginas?.metodosPago ?? data.metodosPago ?? "",
            sobreNosotros: {
              tipo: snTipo,
              texto: snTexto,
              url: snUrl,
            },
          });
        }
      })
      .catch(() => message.error("Error al cargar configuración"))
      .finally(() => this.setState({ loading: false }));
  };

  consultarMarcas = async () => {
    axios
      .post(
        "marcas/selectMarcas",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (res.data.success === 2 && res.data) {
          const marcasConLogo = res.data.marcas.filter(
            (marca) => marca.logo && marca.logo.trim() !== "",
          );
          this.setState({ marcasDisponibles: marcasConLogo });
        }
      })
      .catch((error) => {
        console.error("Error al cargar marcas", error);
      });
  };

  consultarEscuelas = () => {
    axios
      .post(
        "escuelas/selectTodasEscuelas",
        { empresaId: this.props.empresaId, valor: 1, pageSize: 1000 },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ escuelasDisponibles: res.data.data || [] });
        }
      })
      .catch(() => {});
  };

  descartarCambios = () => {
    this.setState({ formConCambios: false });
    this.consultarConfiguracion();
  };

  onFinish = async (values) => {
    this.setState({ loading: true });
    try {
      // Cada sección (Términos, Aviso, Envíos, Métodos de Pago, etc.) se
      // renderiza dentro del SidebarMenu y SOLO la pestaña activa está montada.
      // Tanto `onFinish(values)` como `validateFields()` devuelven únicamente
      // los campos montados, por lo que las pestañas nunca abiertas llegarían
      // como `undefined` y se guardarían vacías (perdiendo su contenido). Por
      // eso leemos el store COMPLETO del form para conservar todas las páginas.
      const valores = this.formRef.current
        ? this.formRef.current.getFieldsValue(true)
        : values;

      const zonasProcesadas = await procesarZonas(valores?.inicio?.zonas || []);
      const layoutProcesado = await procesarLayout(valores?.layout || {});

      const payload = {
        empresaId: this.props.empresaId,
        layout: layoutProcesado,
        terminos: valores.terminos ?? "",
        aviso: valores.aviso ?? "",
        enviosDevoluciones: valores.enviosDevoluciones ?? "",
        metodosPago: valores.metodosPago ?? "",
        sobreNosotros: valores.sobreNosotros || {
          tipo: "texto",
          texto: "",
          url: "",
        },
      };

      // Solo incluye inicio.zonas si hay algo que guardar
      if (zonasProcesadas.length > 0) {
        payload.inicio = { zonas: zonasProcesadas };
      }

      const res = await axios.post(
        "configuracionTiendaOnline/actualizarConfiguracionPaginas",
        payload,
        { headers: this.props.headersToken },
      );

      if (res.data.success === 2) {
        message.success("Configuración guardada exitosamente!");
        this.setState({ formConCambios: false });
      } else {
        message.error(res.data.message || "No se pudo guardar la configuración");
      }
    } catch (error) {
      console.error(error);
      message.error("Error al guardar");
    } finally {
      this.setState({ loading: false });
    }
  };

  tieneCambios = () => this.state.formConCambios;

  guardarDesdePadre = () => {
    return this.formRef.current
      .validateFields()
      .then((values) => this.onFinish(values))
      .catch(() => false);
  };

  handleValuesChange = () => {
    if (!this.state.formConCambios) this.setState({ formConCambios: true });
  };

  normFile = (e) => {
    if (Array.isArray(e)) return e;
    return e && e.fileList;
  };

  normColor = (color) => {
    if (typeof color === "string") return color;
    return color ? color.toHexString() : undefined;
  };

  resetVariante = (zonaIndex) => {
    this.formRef.current?.setFields([
      {
        name: ["inicio", "zonas", zonaIndex, "variante"],
        value: undefined,
      },
      {
        name: ["inicio", "zonas", zonaIndex, "datos"],
        value: {},
      },
    ]);
  };

  resetSubHeaderVariante = () => {
    this.formRef.current?.setFields([
      { name: ["layout", "subHeader", "variante"], value: undefined },
      { name: ["layout", "subHeader", "datos"], value: {} },
    ]);
  };

  resetBannerCarritoDatos = (slot) => {
    this.formRef.current?.setFields([
      { name: ["layout", "bannerCarrito", slot, "datos"], value: {} },
    ]);
  };

  renderZonaPanel = (field, index, helpers) => {
    const { marcasDisponibles } = this.state;
    const { remove, move, totalFields } = helpers;
    const tipoPath = ["inicio", "zonas", field.name, "tipo"];
    const variantePath = ["inicio", "zonas", field.name, "variante"];

    const tituloInternoPath = ["inicio", "zonas", field.name, "tituloInterno"];

    const headerTitulo = (
      <Form.Item
        noStyle
        shouldUpdate={(prev, curr) => {
          const a = prev?.inicio?.zonas?.[field.name];
          const b = curr?.inicio?.zonas?.[field.name];
          return (
            a?.tipo !== b?.tipo ||
            a?.variante !== b?.variante ||
            a?.tituloInterno !== b?.tituloInterno
          );
        }}
      >
        {({ getFieldValue }) => {
          const tipo = getFieldValue(tipoPath);
          const variante = getFieldValue(variantePath);
          const tituloInterno = getFieldValue(tituloInternoPath);
          const baseTitulo = getZonaTitulo(index, tipo, variante);
          return (
            <span style={{ fontWeight: 600 }}>
              {baseTitulo}
              {tituloInterno && (
                <span
                  style={{
                    fontWeight: 400,
                    color: "#888",
                    marginLeft: 8,
                  }}
                >
                  — {tituloInterno}
                </span>
              )}
            </span>
          );
        }}
      </Form.Item>
    );

    const headerExtra = (
      <div onClick={(e) => e.stopPropagation()}>
        <Space size={6}>
          <ButtonItem
            icon={<ArrowUpOutlined />}
            onClick={() => move(index, index - 1)}
            disabled={index === 0}
            blanco
            lessTime
          />
          <ButtonItem
            icon={<ArrowDownOutlined />}
            onClick={() => move(index, index + 1)}
            disabled={index === totalFields - 1}
            blanco
            lessTime
          />
          <ButtonItem
            icon={<DeleteOutlined />}
            onClick={() => remove(field.name)}
            rojo
            lessTime
          />
        </Space>
      </div>
    );

    return (
      <Panel
        key={String(field.key)}
        header={headerTitulo}
        extra={headerExtra}
        style={{ background: "#fff", marginBottom: 12, borderRadius: 6 }}
      >
        <Row gutter={12} align="middle">
          <Col span={16}>
            <FormItem
              label="Tipo de Componente"
              name={[field.name, "tipo"]}
              tooltip="Define qué se renderizará en esta zona."
              required
            >
              <SelectItem
                placeholder="Selecciona el componente"
                options={ZONA_OPTIONS}
                onChange={() => this.resetVariante(field.name)}
              />
            </FormItem>
          </Col>
          <Col span={8}>
            <Row justify="end" align="middle">
              <span style={{ fontWeight: "bold", marginRight: 8 }}>
                Mostrar
              </span>
              <FormItem
                name={[field.name, "mostrar"]}
                valuePropName="checked"
                margin={false}
                initialValue={true}
              >
                <SwitchItem />
              </FormItem>
            </Row>
          </Col>
        </Row>

        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) => {
            const a = prev?.inicio?.zonas?.[field.name];
            const b = curr?.inicio?.zonas?.[field.name];
            return a?.tipo !== b?.tipo || a?.variante !== b?.variante;
          }}
        >
          {({ getFieldValue }) => {
            const tipo = getFieldValue(tipoPath);
            if (!tipo) return null;
            const variante = getFieldValue(variantePath);
            const descripcion = getVarianteDescripcion(tipo, variante);
            return (
              <>
                <FormItem
                  label="Variante"
                  name={[field.name, "variante"]}
                  tooltip="Selecciona el estilo o composición específica."
                  required
                >
                  <SelectItem
                    placeholder="Selecciona la variante"
                    options={getVarianteOptions(tipo)}
                  />
                </FormItem>
                {descripcion && (
                  <div
                    style={{
                      background: "#f5fbfd",
                      borderLeft: "3px solid #00BFD6",
                      padding: "8px 12px",
                      borderRadius: 4,
                      marginBottom: 16,
                      fontSize: 12,
                      color: "#555",
                      lineHeight: 1.5,
                    }}
                  >
                    {descripcion}
                  </div>
                )}
              </>
            );
          }}
        </Form.Item>

        <FormItem
          label="Título interno (opcional)"
          name={[field.name, "tituloInterno"]}
          tooltip="Etiqueta para identificar esta zona internamente."
        >
          <InputItem placeholder="Ej: Zona promocional verano" />
        </FormItem>

        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) => {
            const a = prev?.inicio?.zonas?.[field.name];
            const b = curr?.inicio?.zonas?.[field.name];
            return a?.tipo !== b?.tipo || a?.variante !== b?.variante;
          }}
        >
          {({ getFieldValue }) => {
            const tipo = getFieldValue(tipoPath);
            const variante = getFieldValue(variantePath);
            if (!tipo || !variante) return null;
            return (
              <ZonaFieldsRenderer
                tipo={tipo}
                variante={variante}
                parentName={[field.name, "datos"]}
                marcasDisponibles={marcasDisponibles}
                normFile={this.normFile}
                normColor={this.normColor}
              />
            );
          }}
        </Form.Item>
      </Panel>
    );
  };

  renderContenidoInicio() {
    return (
      <Form.List name={["inicio", "zonas"]}>
        {(fields, { add, remove, move }) => (
          <>
            {fields.length === 0 && (
              <div style={{ margin: "0 auto 12px auto", maxWidth: "98%" }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Aún no hay zonas. Agrega la primera para empezar."
                />
              </div>
            )}

            {fields.length > 0 && (
              <div style={{ margin: "0 auto 0 auto", maxWidth: "98%" }}>
                <Collapse
                  bordered={false}
                  style={{ background: "transparent" }}
                  expandIconPosition="start"
                >
                  {fields.map((field, index) =>
                    this.renderZonaPanel(field, index, {
                      remove,
                      move,
                      totalFields: fields.length,
                    }),
                  )}
                </Collapse>
              </div>
            )}

            <div style={{ margin: "12px auto 12px auto", maxWidth: "98%" }}>
              <ButtonItem
                icon={<PlusOutlined />}
                onClick={() =>
                  add({
                    tipo: undefined,
                    variante: undefined,
                    mostrar: true,
                    tituloInterno: "",
                    datos: {},
                  })
                }
                cyan
                style={{ display: "block", margin: "0 0 0 auto" }}
              >
                Agregar Zona
              </ButtonItem>
            </div>
          </>
        )}
      </Form.List>
    );
  }

  renderLayoutSlot(slot, titulo) {
    const variantePath = ["layout", slot, "variante"];
    const opciones = getLayoutVarianteOptions(slot);

    return (
      <DynamicCardForm
        title={titulo}
        style={{ margin: "0 auto 12px auto", maxWidth: "98%" }}
      >
        <FormItem
          label={`Variante de ${titulo}`}
          name={variantePath}
          tooltip={`Selecciona el componente de ${titulo.toLowerCase()} a usar.`}
          required
        >
          <SelectItem
            placeholder={`Selecciona variante de ${titulo.toLowerCase()}`}
            options={opciones}
          />
        </FormItem>

        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) =>
            prev?.layout?.[slot]?.variante !== curr?.layout?.[slot]?.variante
          }
        >
          {({ getFieldValue }) => {
            const variante = getFieldValue(variantePath);
            const descripcion = getLayoutVarianteDescripcion(slot, variante);
            const fields = getLayoutVarianteFields(slot, variante);
            return (
              <>
                {descripcion && (
                  <div
                    style={{
                      background: "#f5fbfd",
                      borderLeft: "3px solid #00BFD6",
                      padding: "8px 12px",
                      borderRadius: 4,
                      marginBottom: 16,
                      fontSize: 12,
                      color: "#555",
                      lineHeight: 1.5,
                    }}
                  >
                    {descripcion}
                  </div>
                )}
                {variante && fields.length === 0 && (
                  <div style={{ fontSize: 12, color: "#888" }}>
                    Esta variante no requiere configuración adicional por el
                    momento.
                  </div>
                )}
              </>
            );
          }}
        </Form.Item>
      </DynamicCardForm>
    );
  }

  renderSubHeader() {
    const { marcasDisponibles } = this.state;
    const tipoOpciones = getSubHeaderTipoOptions();
    const activoPath = ["layout", "subHeader", "activo"];
    const tipoPath = ["layout", "subHeader", "tipo"];
    const variantePath = ["layout", "subHeader", "variante"];

    return (
      <DynamicCardForm
        title="SubHeader"
        style={{ margin: "0 auto 12px auto", maxWidth: "98%" }}
      >
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <span style={{ fontWeight: "bold" }}>Activar SubHeader</span>
          <FormItem name={activoPath} valuePropName="checked" margin={false}>
            <SwitchItem />
          </FormItem>
        </Row>

        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) =>
            prev?.layout?.subHeader?.activo !== curr?.layout?.subHeader?.activo
          }
        >
          {({ getFieldValue }) => {
            const activo = getFieldValue(activoPath);
            if (!activo) return null;

            return (
              <>
                <FormItem
                  label="Tipo de componente"
                  name={tipoPath}
                  tooltip="Componente a renderizar en el subHeader."
                  required
                >
                  <SelectItem
                    placeholder="Selecciona el componente"
                    options={tipoOpciones}
                    onChange={() => this.resetSubHeaderVariante()}
                  />
                </FormItem>

                <Form.Item
                  noStyle
                  shouldUpdate={(prev, curr) => {
                    const a = prev?.layout?.subHeader;
                    const b = curr?.layout?.subHeader;
                    return a?.tipo !== b?.tipo || a?.variante !== b?.variante;
                  }}
                >
                  {({ getFieldValue }) => {
                    const tipo = getFieldValue(tipoPath);
                    if (!tipo) return null;
                    const variante = getFieldValue(variantePath);
                    const descripcion = getVarianteDescripcion(tipo, variante);
                    return (
                      <>
                        <FormItem
                          label="Variante"
                          name={variantePath}
                          tooltip="Selecciona el estilo o composición específica."
                          required
                        >
                          <SelectItem
                            placeholder="Selecciona la variante"
                            options={getVarianteOptions(tipo)}
                          />
                        </FormItem>
                        {descripcion && (
                          <div
                            style={{
                              background: "#f5fbfd",
                              borderLeft: "3px solid #00BFD6",
                              padding: "8px 12px",
                              borderRadius: 4,
                              marginBottom: 16,
                              fontSize: 12,
                              color: "#555",
                              lineHeight: 1.5,
                            }}
                          >
                            {descripcion}
                          </div>
                        )}
                        {tipo && variante && (
                          <ZonaFieldsRenderer
                            tipo={tipo}
                            variante={variante}
                            parentName={["layout", "subHeader", "datos"]}
                            marcasDisponibles={marcasDisponibles}
                            normFile={this.normFile}
                            normColor={this.normColor}
                          />
                        )}
                      </>
                    );
                  }}
                </Form.Item>
              </>
            );
          }}
        </Form.Item>
      </DynamicCardForm>
    );
  }

  renderLayout() {
    return (
      <>
        {this.renderLayoutSlot("header", "Header")}
        {this.renderSubHeader()}
        {this.renderLayoutSlot("footer", "Footer")}
      </>
    );
  }

  renderBannerSlot(slot, titulo) {
    const { marcasDisponibles } = this.state;
    const activoPath = ["layout", "bannerCarrito", slot, "activo"];
    const variantePath = ["layout", "bannerCarrito", slot, "variante"];

    return (
      <DynamicCardForm
        title={titulo}
        style={{ margin: "0 auto 12px auto", maxWidth: "98%" }}
        collapsable
        defaultOpen={false}
      >
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <span style={{ fontWeight: "bold" }}>Mostrar este banner</span>
          <FormItem name={activoPath} valuePropName="checked" margin={false}>
            <SwitchItem />
          </FormItem>
        </Row>

        <Form.Item
          noStyle
          shouldUpdate={(prev, curr) =>
            prev?.layout?.bannerCarrito?.[slot]?.activo !==
            curr?.layout?.bannerCarrito?.[slot]?.activo
          }
        >
          {({ getFieldValue }) => {
            const activo = getFieldValue(activoPath);
            if (!activo) return null;

            return (
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) =>
                  prev?.layout?.bannerCarrito?.[slot]?.variante !==
                  curr?.layout?.bannerCarrito?.[slot]?.variante
                }
              >
                {({ getFieldValue: getFV }) => {
                  const variante = getFV(variantePath);
                  const descripcion = getVarianteDescripcion(
                    "bannerCarrito",
                    variante,
                  );
                  return (
                    <>
                      <FormItem
                        label="Tipo de banner"
                        name={variantePath}
                        tooltip="Elige si será una sola imagen alargada o tres imágenes unidas."
                        required
                      >
                        <SelectItem
                          placeholder="Selecciona el tipo de banner"
                          options={getVarianteOptions("bannerCarrito")}
                          onChange={() => this.resetBannerCarritoDatos(slot)}
                        />
                      </FormItem>
                      {descripcion && (
                        <div
                          style={{
                            background: "#f5fbfd",
                            borderLeft: "3px solid #00BFD6",
                            padding: "8px 12px",
                            borderRadius: 4,
                            marginBottom: 16,
                            fontSize: 12,
                            color: "#555",
                            lineHeight: 1.5,
                          }}
                        >
                          {descripcion}
                        </div>
                      )}
                      {variante && (
                        <ZonaFieldsRenderer
                          tipo="bannerCarrito"
                          variante={variante}
                          parentName={[
                            "layout",
                            "bannerCarrito",
                            slot,
                            "datos",
                          ]}
                          marcasDisponibles={marcasDisponibles}
                          normFile={this.normFile}
                          normColor={this.normColor}
                        />
                      )}
                    </>
                  );
                }}
              </Form.Item>
            );
          }}
        </Form.Item>
      </DynamicCardForm>
    );
  }

  renderBannerCarrito() {
    const { escuelasDisponibles } = this.state;
    const isSchool = this.props.escuela;

    return (
      <>
        <div
          style={{
            margin: "0 auto 4px auto",
            maxWidth: "98%",
            fontWeight: 600,
            fontSize: 14,
            color: "#333",
          }}
        >
          Banners Generales
        </div>
        <div
          style={{
            margin: "0 auto 12px auto",
            maxWidth: "98%",
            background: "#f5fbfd",
            borderLeft: "3px solid #00BFD6",
            padding: "10px 14px",
            borderRadius: 4,
            fontSize: 13,
            color: "#555",
            lineHeight: 1.5,
          }}
        >
          Estos banners se muestran de extremo a extremo en las páginas de
          Carrito y Checkout. El banner superior aparece debajo del header y el
          inferior al final de la página.
          {isSchool && (
            <>
              {" "}Se muestran para:{" "}
              <strong>usuarios sin cuenta</strong> y{" "}
              <strong>
                usuarios que crearon su cuenta por sí mismos
              </strong>{" "}
              (no alumnos vinculados a una escuela).
            </>
          )}
        </div>
        {this.renderBannerSlot("superior", "Banner superior (bajo el header)")}
        {this.renderBannerSlot("inferior", "Banner inferior (final de la página)")}

        {isSchool && escuelasDisponibles.length > 0 && (
          <>
            <div
              style={{
                margin: "20px auto 8px auto",
                maxWidth: "98%",
                fontWeight: 600,
                fontSize: 14,
                color: "#333",
              }}
            >
              Banners por Escuela
            </div>
            <div
              style={{
                margin: "0 auto 12px auto",
                maxWidth: "98%",
                background: "#f5fbfd",
                borderLeft: "3px solid #00BFD6",
                padding: "10px 14px",
                borderRadius: 4,
                fontSize: 13,
                color: "#555",
                lineHeight: 1.5,
              }}
            >
              Configura banners específicos para cada escuela. Si una escuela
              no tiene configuración propia, sus estudiantes no verán ningún
              banner en el carrito.
            </div>
            <Collapse
              bordered={false}
              style={{ background: "transparent", maxWidth: "98%", margin: "0 auto" }}
              expandIconPosition="start"
            >
              {escuelasDisponibles.map((escuela) => (
                <Panel
                  key={escuela._id}
                  header={
                    <span style={{ fontWeight: 600 }}>{escuela.nombre}</span>
                  }
                  style={{ background: "#fff", marginBottom: 8, borderRadius: 6 }}
                >
                  <BannerEscuelaCard
                    escuela={escuela}
                    empresaId={this.props.empresaId}
                    headersToken={this.props.headersToken}
                    normFile={this.normFile}
                    normColor={this.normColor}
                  />
                </Panel>
              ))}
            </Collapse>
          </>
        )}
      </>
    );
  }

  renderInicio() {
    return this.renderContenidoInicio();
  }

  renderEditor(name, title, placeholder, extraContent) {
    return (
      <DynamicCardForm
        title={title}
        style={{ margin: "0 auto 12px auto", maxWidth: "98%" }}
      >
        {extraContent}
        <PromptEjemploCopiable />
        <FormItem name={name}>
          <EditorPaginaConHtml placeholder={placeholder} />
        </FormItem>
      </DynamicCardForm>
    );
  }

  renderAviso() {
    const infoCookies = (
      <div
        style={{
          background: "#fffbe6",
          border: "1px solid #ffe58f",
          borderLeft: "4px solid #faad14",
          borderRadius: 6,
          padding: "14px 16px",
          marginBottom: 18,
          fontSize: 13,
          color: "#614700",
          lineHeight: 1.7,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 14 }}>
          Tecnologías de almacenamiento que usa tu tienda
        </div>
        <p style={{ margin: "0 0 10px 0" }}>
          Tu tienda en línea utiliza las siguientes tecnologías de
          almacenamiento local (localStorage / sessionStorage). Aunque no son
          cookies HTTP en sentido estricto, muchas legislaciones de privacidad
          (LFPDPPP, GDPR, etc.) exigen declararlas en tu aviso de privacidad.{" "}
          <strong>Es tu responsabilidad incluirlas.</strong>
        </p>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 12,
            background: "#fff",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <thead>
            <tr style={{ background: "#fef3c7" }}>
              <th
                style={{
                  padding: "6px 10px",
                  textAlign: "left",
                  borderBottom: "1px solid #fde68a",
                  width: "30%",
                }}
              >
                Nombre
              </th>
              <th
                style={{
                  padding: "6px 10px",
                  textAlign: "left",
                  borderBottom: "1px solid #fde68a",
                  width: "15%",
                }}
              >
                Tipo
              </th>
              <th
                style={{
                  padding: "6px 10px",
                  textAlign: "left",
                  borderBottom: "1px solid #fde68a",
                  width: "20%",
                }}
              >
                Duración
              </th>
              <th
                style={{
                  padding: "6px 10px",
                  textAlign: "left",
                  borderBottom: "1px solid #fde68a",
                }}
              >
                Propósito
              </th>
            </tr>
          </thead>
          <tbody>
            {[
              {
                nombre: "ecommerce_token",
                tipo: "localStorage",
                duracion: "7 días",
                proposito:
                  "Token de autenticación del cliente. Permite mantener la sesión activa sin volver a iniciar sesión.",
              },
              {
                nombre: "biwo_cart_{empresaId}",
                tipo: "localStorage",
                duracion: "Persistente",
                proposito:
                  "Carrito de compras. Guarda los productos seleccionados para que no se pierdan al recargar la página.",
              },
              {
                nombre: "conektaSdkDirty",
                tipo: "sessionStorage",
                duracion: "Sesión",
                proposito:
                  "Indicador interno del SDK de Conekta. Se usa para reinicializar el módulo de pago cuando es necesario.",
              },
              {
                nombre: "mattildaPendingSale",
                tipo: "sessionStorage",
                duracion: "Sesión",
                proposito:
                  "Orden pendiente de pago por OXXO/SPEI (Mattilda/Gr4vy). Se conserva hasta confirmar o cancelar el pago.",
              },
              {
                nombre: "mattildaPendingTxId",
                tipo: "sessionStorage",
                duracion: "Sesión",
                proposito:
                  "ID de transacción de retorno de la pasarela Mattilda. Se usa al volver de la página de pago externo.",
              },
            ].map((row, i) => (
              <tr
                key={row.nombre}
                style={{ background: i % 2 === 0 ? "#fff" : "#fffef5" }}
              >
                <td
                  style={{
                    padding: "6px 10px",
                    borderBottom: "1px solid #fde68a",
                    fontFamily: "monospace",
                    wordBreak: "break-all",
                  }}
                >
                  {row.nombre}
                </td>
                <td
                  style={{
                    padding: "6px 10px",
                    borderBottom: "1px solid #fde68a",
                  }}
                >
                  {row.tipo}
                </td>
                <td
                  style={{
                    padding: "6px 10px",
                    borderBottom: "1px solid #fde68a",
                  }}
                >
                  {row.duracion}
                </td>
                <td
                  style={{
                    padding: "6px 10px",
                    borderBottom: "1px solid #fde68a",
                  }}
                >
                  {row.proposito}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ margin: "10px 0 0 0", fontSize: 12, color: "#7c5c00" }}>
          Nota: ninguna de estas tecnologías rastrea al usuario entre sitios web
          de terceros ni comparte datos con anunciantes.
        </p>
      </div>
    );

    return this.renderEditor(
      "aviso",
      "Aviso de Privacidad",
      "Escribe el aviso de privacidad aquí...",
      infoCookies,
    );
  }

  renderSobreNosotros() {
    return (
      <DynamicCardForm
        title="Sobre Nosotros"
        style={{ margin: "0 auto 12px auto", maxWidth: "98%" }}
      >
        <FormItem
          name={["sobreNosotros", "tipo"]}
          label="¿Cómo deseas mostrar esta sección?"
          margin
        >
          <Select onChange={(val) => this.setState({ tipoNosotros: val })}>
            <Option value="texto">Redactar contenido aquí</Option>
            <Option value="url">Vincular a una página web externa</Option>
          </Select>
        </FormItem>

        {this.state.tipoNosotros === "texto" ? (
          <FormItem name={["sobreNosotros", "texto"]}>
            <RichTextEditor placeholder="Escribe la historia de tu empresa aquí..." />
          </FormItem>
        ) : (
          <FormItem
            name={["sobreNosotros", "url"]}
            label="Enlace hacia tu sitio web"
            tooltip="Ej: https://mi-empresa.com/nosotros"
          >
            <InputItem placeholder="https://" />
          </FormItem>
        )}
      </DynamicCardForm>
    );
  }

  // Render principal

  render() {
    return (
      <Spin spinning={this.state.loading}>
        <Form
          name="paginasConfig"
          layout="vertical"
          onFinish={this.onFinish}
          onValuesChange={this.handleValuesChange}
          ref={this.formRef}
          preserve={true}
        >
          <SidebarMenu
            menuItems={MENU_ITEMS}
            sectionTitles={SECTION_TITLES}
            defaultSelected="inicio"
            headerLabel="Páginas"
            siderWidth={210}
            minHeight={520}
            renderContent={(key) => {
              switch (key) {
                case "layout":
                  return this.renderLayout();
                case "inicio":
                  return this.renderInicio();
                case "carrito":
                  return this.renderBannerCarrito();
                case "terminos":
                  return this.renderEditor(
                    "terminos",
                    "Términos y Condiciones",
                    "Escribe los términos...",
                  );
                case "aviso":
                  return this.renderAviso();
                case "enviosDevoluciones":
                  return this.renderEditor(
                    "enviosDevoluciones",
                    "Políticas de Envío y Devoluciones",
                    "Escribe las políticas de envío y devoluciones aquí...",
                  );
                case "metodosPago":
                  return this.renderEditor(
                    "metodosPago",
                    "Métodos de Pago",
                    "Escribe las políticas y métodos de pago aceptados aquí...",
                  );
                case "sobreNosotros":
                  return this.renderSobreNosotros();
                default:
                  return null;
              }
            }}
            footer={
              <ButtonItem
                type="primary"
                htmlType="submit"
                style={{
                  minWidth: 160,
                  height: 30,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Guardar Configuración
              </ButtonItem>
            }
          />
        </Form>
      </Spin>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  escuela: state.empresa.escuela,
});

export default connect(mapStateToProps, null, null, { forwardRef: true })(
  PaginasConfiguracion,
);
